import { atom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { atomFamily } from 'jotai-family';
import { atomWithQuery } from 'jotai-tanstack-query';
import isEqual from 'react-fast-compare';
import { SellerUserClient, UserSetting } from 'src/WebApiController';

import { activeAccountAtom } from '../AppContext/AppContext.atom';

export const userSettingsQueryKeyAtom = atom((get) => {
  const { webClientConfig } = get(activeAccountAtom);
  return ['allUserSettingsV2', webClientConfig?.activeAccountId];
});

// Atom with query for user settings
export const userSettingsQueryAtom = atomWithQuery((get) => {
  const { webClientConfig } = get(activeAccountAtom);
  const queryKey = get(userSettingsQueryKeyAtom);
  const shouldQuery = webClientConfig?.activeAccountId != null;

  return {
    queryKey,
    queryFn: async () => {
      if (!shouldQuery || !webClientConfig) {
        return null;
      }

      const sellerUserClient = new SellerUserClient(webClientConfig);
      return await sellerUserClient.getAllSellerUserSettings(
        // These have special handling in the UI so we want to make sure we load them
        // see useUserSetting.ts
        [
          UserSetting.QuickFiltersCustomInventory,
          UserSetting.QuickFiltersCustomSales,
          UserSetting.QuickFiltersCustomPurchases,
          UserSetting.QuickFiltersCustomOnSaleEvent,
          UserSetting.QuickFiltersCustomMarketplacePayments,
        ],
        true
      );
    },
    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  };
});

// Create an atom for just the data portion with stability
// React Query may return new object references even when data is the same
const userSettingsDataAtom = selectAtom(
  userSettingsQueryAtom,
  (queryResult) => queryResult.data,
  // Deep equality check - only update if the actual data changed
  isEqual
);

// atomFamily to get a specific user setting by UserSetting enum value
// Uses selectAtom to only trigger re-renders when the SPECIFIC setting value changes
// We want to differentiate between null(not loaded) and undefined (loaded but value is undefined) for better loading state handling in the UI
export const userSettingsFamilyAtom = atomFamily(
  (userSetting: UserSetting | undefined) => {
    if (userSetting === undefined) {
      return atom(() => undefined);
    }

    // selectAtom provides built-in equality checking - only rerenders when the selected value changes
    return selectAtom(
      userSettingsDataAtom,
      (data) => {
        if (!data) return null;
        return data[userSetting];
      },
      // Custom equality function for string comparison
      isEqual
    );
  }
);
